import { TokenResponseDto } from "@/data/dto/token-response-dto";
import { ChangePasswordByFindLinkPayload } from "@/data/payload/auth/change-password-by-find-link-payload";
import { CheckFindSnPhoneAuthCodePayload } from "@/data/payload/auth/check-find-sn-phone-auth-code-payload";
import { IssueFindSnPhoneAuthCodePayload } from "@/data/payload/auth/issue-find-sn-phone-auth-code-payload";
import { IssueWebFindSnEmailAuthCodePayload } from "@/data/payload/auth/issue-web-find-sn-email-auth-code-payload";
import { IssueWebFindSnLinkPayload } from "@/data/payload/auth/issue-web-find-sn-link-payload";
import { URLList } from "@/data/url_list";
import { createLogger, withApiLogging } from "@/utils/logger/logger";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATResult } from "@sadtx/sat-error";

export class UserPasswordSource {
  private readonly logger = createLogger("UserPasswordSource");

  constructor(private readonly client: FetchApiClientMethod) {}

  async issueWebFindSnLink(
    payload: IssueWebFindSnLinkPayload,
  ): Promise<SATResult<void, SATError>> {
    const url = URLList.AUTH.ISSUE_WEB_FIND_SN_LINK;
    try {
      await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  /** @deprecated Prefer issueWebFindSnLink */
  async sendResetSnEmail(email: string): Promise<SATResult<void, SATError>> {
    return this.issueWebFindSnLink({ email });
  }

  async issueFindSnPhoneAuthCode(
    payload: IssueFindSnPhoneAuthCodePayload,
  ): Promise<SATResult<TokenResponseDto, SATError>> {
    const url = URLList.AUTH.ISSUE_FIND_SN_CODE;
    try {
      const data = await withApiLogging(this.logger, { method: "POST", url, payload }, async () =>
        this.request(url, {
          method: "POST",
          body: JSON.stringify(payload),
        }),
      );
      return SATResult.success(readTokenResponse(data));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async issueWebFindSnEmailAuthCode(
    payload: IssueWebFindSnEmailAuthCodePayload,
  ): Promise<SATResult<TokenResponseDto, SATError>> {
    const url = URLList.AUTH.ISSUE_WEB_FIND_SN_EMAIL_CODE;
    try {
      const data = await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data;
      });
      return SATResult.success(readTokenResponse(data));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async checkFindSnPhoneAuthCode(
    payload: CheckFindSnPhoneAuthCodePayload,
  ): Promise<SATResult<TokenResponseDto, SATError>> {
    const url = URLList.AUTH.CHECK_FIND_SN_CODE;
    try {
      const data = await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data;
      });
      return SATResult.success(readTokenResponse(data));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<SATResult<void, SATError>> {
    const url = URLList.USER.CHANGE_PASSWORD;
    const payload = {
      sn: oldPassword,
      newSn: newPassword,
    };
    try {
      await withApiLogging(this.logger, { method: "PUT", url, payload }, async () => {
        const response = await this.client.put(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async initPasswordByAccountSetupToken(
    token: string,
    newPassword: string,
  ): Promise<SATResult<void, SATError>> {
    try {
      await this.request(URLList.AUTH.INIT_SN_ON_ACCOUNT_SETUP_LINK, {
        method: "PUT",
        body: JSON.stringify({
          token,
          newSn: newPassword,
        }),
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async changePasswordByVerificationToken(
    token: string,
    newPassword: string,
  ): Promise<SATResult<void, SATError>> {
    const url = URLList.AUTH.CHANGE_PASSWORD_BY_VERIFICATION;
    const payload = {
      token,
      newSn: newPassword,
    };
    try {
      await withApiLogging(this.logger, { method: "PUT", url, payload }, async () =>
        this.request(url, {
          method: "PUT",
          body: JSON.stringify(payload),
        }),
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async changePasswordByFindLink(
    payload: ChangePasswordByFindLinkPayload,
  ): Promise<SATResult<void, SATError>> {
    const url = URLList.AUTH.CHANGE_PASSWORD_BY_FIND_LINK;
    try {
      await withApiLogging(this.logger, { method: "PUT", url, payload }, async () => {
        const response = await this.client.put(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  private async request(url: string, init: RequestInit): Promise<unknown> {
    const response = await fetch(url, {
      ...init,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(init.headers ?? {}),
      },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw { response: { data, status: response.status } };
    }
    return response.json().catch(() => ({}));
  }
}

function readTokenResponse(value: unknown): TokenResponseDto {
  const data = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  return {
    token: typeof data.token === "string" ? data.token : "",
  };
}
